import React, { useMemo } from 'react'
import { Helmet } from 'react-helmet-async'

import { BreadcrumbList } from 'schema-dts'

import { t } from 'src/translations'
import SchemaThing from 'src/public/components/seo/SchemaThing'
import { useModal } from 'shared/components/common/modal'
import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'
import { useRestaurantRoutes } from 'shared/components/common/restaurant_routes/RestaurantRoutesContext'

import { getMenuItemSrcSet } from 'public/components/default_template/menu_section/MenuItemCard'
import { MenuItem } from 'public/components/default_template/menu_section/MenuSection'
import ItemModal from 'public/components/default_template/online_ordering/item_modal/ItemModal'
import { ItemSource } from '../cart/cartUtils'

const MAX_META_DESCRIPTION_LENGTH = 160

const DIETARY_LABEL_ITEMS: Record<string, string> = {
  // vegan labels
  vegan: 'Vegan',
  vg: 'Vegan',

  // vegetarian labels
  vegetarian: 'Vegetarian',
  veggie: 'Vegetarian',

  // gluten labels
  glutenfree: 'Gluten-Free',
  gf: 'Gluten-Free',

  // dairy labels
  dairyfree: 'Dairy-Free',
  df: 'Dairy-Free',

  // kosher labels
  kosher: 'Kosher',

  // halal labels
  halal: 'Halal'
}

const normalizeTagName = (name: string) =>
  name.toLowerCase().replace(/[^a-z0-9]/g, '')

export const getMetaDescription = (
  item: MenuItem,
  restaurantName: string | undefined | null,
  location: { city?: string | null; state?: string | null }
) => {
  const locationStr = [location.city, location.state].filter(Boolean).join(', ')

  const existingText = normalizeTagName(
    `${item.name ?? ''} ${item.description ?? ''}`
  )
  const dietaryLabels = [
    ...new Set(
      item.itemTags
        ?.map((tag) => DIETARY_LABEL_ITEMS[normalizeTagName(tag.name ?? '')])
        .filter(
          (label) => label && !existingText.includes(normalizeTagName(label))
        )
    )
  ].join(', ')

  return [
    `Order ${item.name} online from ${restaurantName}${
      locationStr ? ` in ${locationStr}` : ''
    }.`,
    dietaryLabels ? `${dietaryLabels}.` : '',
    item.description ?? ''
  ]
    .filter(Boolean)
    .join(' ')
    .trimEnd()
    .substring(0, MAX_META_DESCRIPTION_LENGTH)
}

const DeeplinkItemModal = ({ item }: { item: MenuItem }) => {
  const { ooRestaurant, selectedLocation, restaurant } = useRestaurant()
  const { orderPath } = useRestaurantRoutes()
  const { isOpen, onClose } = useModal({ defaultOpen: true })

  const displayedRestaurantName = selectedLocation.name || ooRestaurant?.name

  const brandName = restaurant?.name || displayedRestaurantName
  const orderPageTitle =
    selectedLocation?.orderPageConfigOverride?.seoMeta?.title ||
    restaurant?.config?.ooConfig?.seoMeta?.title ||
    t('order-page-wrapper.order-online')
  const metaTitle = `${displayedRestaurantName} - ${item.name}`
  const metaDescription = getMetaDescription(
    item,
    displayedRestaurantName,
    selectedLocation
  )
  const imageSet = useMemo(
    () => (item.imageUrls ? getMenuItemSrcSet(item.imageUrls) : {}),
    [item.imageUrls]
  )
  const openGraphImage = item.image ?? imageSet['600w'] ?? imageSet['900w']

  const restaurantGuid = ooRestaurant?.guid
  const breadcrumbSchema = useMemo(() => {
    if (!restaurantGuid) {
      return null
    }
    // parentSlugs only exists for ecommerce (its menu categories are real
    // pages); for regular online ordering it's empty, so those category crumbs
    // are simply skipped and the breadcrumb is Restaurant -> Order -> Item.
    const parentSlugs = item.parentSlugs ?? []
    return (
      <SchemaThing<BreadcrumbList>
        json={(routes) => ({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: brandName,
              item: routes.homeUrl
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: orderPageTitle,
              item: routes.orderUrl(restaurantGuid)
            },
            ...parentSlugs.map((parent, i) => ({
              '@type': 'ListItem' as const,
              position: i + 3,
              name: parent.name
            })),
            // The current item is the last crumb and omits `item` per spec.
            {
              '@type': 'ListItem',
              position: parentSlugs.length + 3,
              name: item.name ?? ''
            }
          ]
        })}
      />
    )
  }, [item.parentSlugs, restaurantGuid, brandName, orderPageTitle, item.name])

  return (
    <div data-testid='deeplink-item-modal'>
      {breadcrumbSchema}
      <Helmet>
        <title>{metaTitle}</title>
        <meta name='description' content={metaDescription} />
        <meta property='og:title' content={metaTitle} />
        <meta property='og:description' content={metaDescription} />
        {openGraphImage && (
          <meta property='og:image' content={openGraphImage} />
        )}
      </Helmet>
      <ItemModal
        withTransitions={false}
        isOpen={isOpen}
        onClose={() => {
          onClose()
          window.history.replaceState(null, '', orderPath)
        }}
        itemGroupGuid={item.itemGroupGuid}
        itemGuid={item.guid}
        itemTags={item.itemTags}
        itemSource={ItemSource.Menu}
      />
    </div>
  )
}

export default DeeplinkItemModal
